import { useDispatch, useSelector } from "react-redux";
import {
  getWeeklySummaryActionsState,
  SummaryAction,
  SummaryChallenge,
  SummaryGoal,
  SummaryIssueIdentify,
  SummaryReason,
  SummarySupportResource,
  WeeklySummaryActionsRequest,
  weeklySummaryActionsStateAction,
  WeeklySummaryResponse,
} from "../../../redux/features/weekly-summary-slice";
import Input from "../../../components/input";
import { summaryOptions } from "../../../core/utils/constants";
import { useEffect, useState } from "react";
import SummaryPropertyDiscussDialog from "../../weekly-report/components/summary-property-discuss-dialog";
import SwitchTab from "../../../components/switch-tab";
import Button from "../../../components/button";
import EmptyDataOverlay from "../../../components/empty-data-overlay";
import { getUserState } from "../../../redux/features/auth-slice";
import { AppDispatch } from "../../../redux/store";
import WeeklySummaryActionForm from "./weekly-summary-action-form";

interface Props {
  summary: WeeklySummaryResponse;
  selectedOption: string;
  setSelectedOption: (value: string) => void;
  onRefresh: () => void;
}

export default function WeeklySummaryForm(props: Props) {
  const { summary, selectedOption, setSelectedOption, onRefresh } = props;

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const weeklySummaryActionsState = useSelector(getWeeklySummaryActionsState);

  const [discussProp, setDiscussProp] = useState<any | null>(null);
  const [discussPropType, setDiscussPropType] = useState<
    "reason" | "challenge" | "issue" | "goal" | "resource" | null
  >(null);
  const [actions, setActions] = useState<SummaryAction[]>([]);

  const submitActions = async (values: WeeklySummaryActionsRequest) => {
    await dispatch(weeklySummaryActionsStateAction(values));
  };

  const renderMenuItem = (label: string, disabled: boolean = false) => {
    return (
      <label
        className={`w-fit p-3 mb-2 text-sm hover:bg-gray-100 rounded-lg cursor-pointer ${
          selectedOption === label ? "bg-gray-100" : "bg-background-paper"
        } ${disabled ? "text-action-disabled" : "text-text-title"}`}
        onClick={() => !disabled && setSelectedOption(label)}
      >
        {label}
      </label>
    );
  };

  const renderForm = () => {
    switch (selectedOption) {
      case summaryOptions[0]:
        if (summary.reasons.length === 0) {
          return (
            <EmptyDataOverlay
              key="emptyReasons"
              label={`No "${summaryOptions[0]}" found`}
              exClassName="h-full text-xs"
            />
          );
        }
        return (
          <div key="reasons" className="flex flex-col space-y-4">
            {summary.reasons.map((rea: SummaryReason) => {
              return (
                <div
                  key={rea.type.id}
                  className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
                >
                  <div className="w-full flex flex-col space-y-4">
                    <div>
                      <label className="text-xs text-text-title">Type</label>
                      <Input type="text" value={rea.type.title} readonly />
                    </div>
                    <div>
                      <label className="text-xs text-text-title">Detail</label>
                      <Input
                        type="text"
                        inputType="textarea"
                        rows={3}
                        value={rea.detail}
                        readonly
                      />
                    </div>
                  </div>
                  <Button
                    variant="outlined"
                    label={`Discusses (${rea.discuss.length})`}
                    onClick={() => {
                      setDiscussProp(rea);
                      setDiscussPropType("reason");
                    }}
                    exClassName="w-fit self-end border-divider"
                  />
                </div>
              );
            })}
          </div>
        );
      case summaryOptions[1]:
        if (summary.challenges.length === 0) {
          return (
            <EmptyDataOverlay
              key="emptyChallenges"
              label={`No "${summaryOptions[1]}" found`}
              exClassName="h-full text-xs"
            />
          );
        }
        return (
          <div key="challenges" className="flex flex-col space-y-4">
            {summary.challenges.map((chal: SummaryChallenge) => (
              <div
                key={chal.type.id}
                className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
              >
                <div className="w-full flex flex-col space-y-4">
                  <div>
                    <label className="text-xs text-text-title">Type</label>
                    <Input type="text" value={chal.type.title} readonly />
                  </div>
                  <div>
                    <label className="text-xs text-text-title">Explain</label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={chal.explain}
                      readonly
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-title">
                      Action / Proposed
                    </label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={chal.action}
                      readonly
                    />
                  </div>
                </div>
                {chal.type.id !== "null" && (
                  <Button
                    variant="outlined"
                    label={`Discusses (${chal.discuss.length})`}
                    onClick={() => {
                      setDiscussProp(chal);
                      setDiscussPropType("challenge");
                    }}
                    exClassName="w-fit self-end border-divider"
                  />
                )}
              </div>
            ))}
          </div>
        );
      case summaryOptions[2]:
        if (summary.issueIdentifies.length === 0) {
          return (
            <EmptyDataOverlay
              key="emptyIssues"
              label={`No "${summaryOptions[2]}" found`}
              exClassName="h-full text-xs"
            />
          );
        }
        return (
          <div key="issues" className="flex flex-col space-y-4">
            {summary.issueIdentifies.map((iss: SummaryIssueIdentify) => (
              <div
                key={iss.type.id}
                className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
              >
                <div className="w-full flex flex-col space-y-4">
                  <div>
                    <label className="text-xs text-text-title">Type</label>
                    <Input type="text" value={iss.type.title} readonly />
                  </div>
                  {/* {user &&
                    user.team.id === dcTeam.id &&
                    iss.quantity !== null && (
                      <div>
                        <label className="text-xs text-text-title">
                          Quantity
                        </label>
                        <Input type="text" value={iss.quantity} readonly />
                      </div>
                    )}
                  {user &&
                    user.team.id === dcTeam.id &&
                    iss.jobNumber !== null && (
                      <div>
                        <label className="text-xs text-text-title">
                          Job Number
                        </label>
                        <Input type="text" value={iss.jobNumber} readonly />
                      </div>
                    )} */}
                  <div>
                    <label className="text-xs text-text-title">Reason</label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={iss.reason}
                      readonly
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-title">
                      Action / Proposed
                    </label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={iss.action}
                      readonly
                    />
                  </div>
                </div>
                {iss.type.id !== "null" && (
                  <Button
                    variant="outlined"
                    label={`Discusses (${iss.discuss.length})`}
                    onClick={() => {
                      setDiscussProp(iss);
                      setDiscussPropType("issue");
                    }}
                    exClassName="w-fit self-end border-divider"
                  />
                )}
              </div>
            ))}
          </div>
        );
      case summaryOptions[3]:
        if (summary.goals.length === 0) {
          return (
            <EmptyDataOverlay
              key="emptyGoals"
              label={`No "${summaryOptions[3]}" found`}
              exClassName="h-full text-xs"
            />
          );
        }
        return (
          <div key="goals" className="flex flex-col space-y-4">
            {summary.goals.map((e: SummaryGoal) => (
              <div
                key={e.goal}
                className="flex flex-col items-start p-4 bg-background-default border border-divider space-y-4 rounded-lg"
              >
                <Input
                  type="text"
                  inputType="textarea"
                  rows={3}
                  value={e.goal}
                  readonly
                  exWrapClassName="w-full"
                />
                <Button
                  variant="outlined"
                  label={`Discusses (${e.discuss.length})`}
                  onClick={() => {
                    setDiscussProp(e);
                    setDiscussPropType("goal");
                  }}
                  exClassName="w-fit self-end border-divider"
                />
              </div>
            ))}
          </div>
        );
      case summaryOptions[4]:
        if (summary.supportResources.length === 0) {
          return (
            <EmptyDataOverlay
              key="emptyResources"
              label={`No "${summaryOptions[4]}" found`}
              exClassName="h-full text-xs"
            />
          );
        }
        return (
          <div key="resources" className="flex flex-col space-y-4">
            {summary.supportResources.map((sr: SummarySupportResource) => (
              <div
                key={sr.type.id}
                className="flex flex-col items-center space-y-4 p-4 border border-divider bg-background-default rounded-lg"
              >
                <div className="w-full flex flex-col space-y-4">
                  <div>
                    <label className="w-1/4 text-xs text-text-title">
                      Type
                    </label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={sr.type.title}
                      readonly
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-title">Explain</label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={sr.explain}
                      readonly
                    />
                  </div>
                </div>
                {sr.type.id !== "null" && (
                  <Button
                    variant="outlined"
                    label={`Discusses (${sr.discuss.length})`}
                    onClick={() => {
                      setDiscussProp(sr);
                      setDiscussPropType("resource");
                    }}
                    exClassName="w-fit self-end border-divider"
                  />
                )}
              </div>
            ))}
          </div>
        );
      case "Action Items":
        return (
          <WeeklySummaryActionForm
            key="actions"
            actions={actions}
            setActions={setActions}
          />
        );
    }
  };

  useEffect(() => {
    if (summary.metGoals) {
      setSelectedOption(summaryOptions[1]);
    }
  }, [summary.metGoals]);

  useEffect(() => {
    setActions(summary.actions);
  }, [summary]);

  return (
    <>
      <div className="flex flex-col h-full overflow-scroll space-y-6">
        {/* Met goals */}
        <div className="flex items-center space-x-4">
          <label className="text-md font-medium">
            Did you meet output expectations?
          </label>
          <SwitchTab
            options={["Yes", "No"]}
            tab={summary.metGoals ? "Yes" : "No"}
            setTab={() => {}}
          />
        </div>
        <div className="h-full grid grid-cols-6 gap-4 overflow-hidden">
          <div className="h-full col-span-2 flex flex-col overflow-auto">
            <div className="h-full flex flex-col">
              {renderMenuItem(summaryOptions[0], summary.metGoals)}
              {renderMenuItem(summaryOptions[1])}
              {renderMenuItem(summaryOptions[2])}
              {renderMenuItem(summaryOptions[3])}
              {renderMenuItem(summaryOptions[4])}
              {renderMenuItem("Action Items")}
            </div>
            <Button
              label="Submit Actions"
              loading={weeklySummaryActionsState.loading}
              onClick={() => {
                submitActions({ summaryId: summary.id, actions: actions });
              }}
              exClassName="w-fit"
            />
          </div>
          <div className="h-full col-span-4 flex flex-col overflow-y-scroll">
            {renderForm()}
          </div>
        </div>
      </div>
      <SummaryPropertyDiscussDialog
        summaryId={summary.id}
        type={discussPropType}
        property={discussProp}
        setProperty={setDiscussProp}
        onSubmitted={onRefresh}
      />
    </>
  );
}
